import { useWhiteLabelSafe } from '@/context/white-label';
import { useIsCmdShiftHOverlayEnabled } from '@/features/flags';
import { isTauri } from '@/tauri/utils';
import {
  WorkHomeBelowComposer as PackageWorkHomeBelowComposer,
  WorkHomeEmptyState as PackageWorkHomeEmptyState,
  WorkShortcutTip,
} from '@heidi/agents';
import platform from 'platform';
import type { ReactNode } from 'react';

import { WorkContextSuggestions } from '@heidi/agents';
import type { WorkMode } from '@heidi/agents/contracts';
import type { WorkHomeFocusContext } from '@heidi/agents/data';
import { AssistantText } from '../timeline/projectTimelineBlocks';
import { RoutinesDesktopNurtureBanner } from './RoutinesDesktopNurtureBanner';

/**
 * The Work-home chrome's HOST. The chrome itself is `@heidi/agents`; this file
 * is what supplies it the five children a package cannot hold, and the prop
 * signatures are unchanged so the route and its suite name the same API.
 *
 * WHAT STAYS HERE AND WHY, per child:
 *   `AssistantText`                  reaches `@/components/chat`
 *   `RoutinesDesktopNurtureBanner`   jotai atoms, a flag, `isTauri`, a query
 *   `WorkContextSuggestions`         app-side today; a slot costs nothing extra
 *   `WorkShortcutTip`                moved, but its two gates are app-resolved
 *
 * `WorkWaysToWork` used to be on that list and is not any more. The rail moved to
 * the landing's FOOT, past the page's slack, so it is no longer this chrome's
 * child at all: the route binds `WorkHomeRoutineDock` off the package door and
 * hands it the rail directly. The route may — it is inside the Chat globs, which
 * is the same reason the Evidence starters are supplied there rather than here.
 *
 * The Evidence starters are the one child this host does NOT build. They are a
 * Chat component, and this file is product-domain side, so the node is supplied
 * by the Work route — which is inside the Chat globs and may import it — and
 * passes straight through. A slot here would buy nothing on its own; what makes
 * it work is that the supplier sits on the far side of the quarantine fence.
 *
 * The tip is the interesting one: the component moved and the GATES did not.
 * `isTauri()` is a platform probe, `useIsCmdShiftHOverlayEnabled` is a flag
 * resolver, and the modifier glyph is an OS read — three things that make a
 * surface unmountable outside this app. They are computed here and crossed as
 * booleans, which is why the tip itself could travel.
 *
 * `WorkHomeComposerGlow` is NOT re-exported: it is fully portable, so the route
 * binds it straight off the package door.
 */

const isMacOS = (): boolean => {
  const family = platform.os?.family?.toLowerCase();
  return family?.includes('mac') === true || platform.os?.family === 'OS X';
};

export interface WorkHomeEmptyStateProps {
  isContextFocused: boolean;
  workMode: WorkMode;
  firstName?: string;
  focus: WorkHomeFocusContext;
  disabled: boolean;
  onBack: () => void;
  onSelectPrompt: (prompt: string) => void;
}

export const WorkHomeEmptyState = ({
  isContextFocused,
  workMode,
  firstName,
  focus,
  disabled,
  onBack,
  onSelectPrompt,
}: WorkHomeEmptyStateProps): JSX.Element => {
  const { isWhiteLabel } = useWhiteLabelSafe();
  return (
    <PackageWorkHomeEmptyState
      isContextFocused={isContextFocused}
      workMode={workMode}
      firstName={firstName}
      showBrandMark={!isWhiteLabel}
      onBack={onBack}
      assistantText={<AssistantText text={focus.starterText} streaming={false} />}
      contextSuggestions={
        <WorkContextSuggestions
          kind={focus.kind}
          onSelect={onSelectPrompt}
          disabled={disabled}
          className="max-w-none justify-start"
        />
      }
    />
  );
};

export interface WorkHomeBelowComposerProps {
  workMode: WorkMode;
  /** Composer-only Evidence mode hides Work prompt suggestions without
   *  changing the page-level hero, nurture banner, or routine dock. */
  showSuggestions?: boolean;
  /** Starter slot gate, independent of the page mode — see the package prop. */
  showEvidenceStarters?: boolean;
  reserveSuggestionSpace?: boolean;
  isContextFocused: boolean;
  hasStarted: boolean;
  disabled: boolean;
  onSelectSuggestion: (prompt: string, suggestionId?: string) => void;
  onSuggestionsShown?: (ids: readonly string[]) => void;
  /** Route-supplied Evidence-mode starters — see the note above. */
  evidenceStarters: ReactNode;
}

export const WorkHomeBelowComposer = ({
  workMode,
  showSuggestions,
  showEvidenceStarters,
  reserveSuggestionSpace,
  isContextFocused,
  hasStarted,
  disabled,
  onSelectSuggestion,
  onSuggestionsShown,
  evidenceStarters,
}: WorkHomeBelowComposerProps): JSX.Element => {
  const isCmdShiftHOverlayEnabled = useIsCmdShiftHOverlayEnabled();

  return (
    <PackageWorkHomeBelowComposer
      workMode={workMode}
      showSuggestions={showSuggestions}
      showEvidenceStarters={showEvidenceStarters}
      reserveSuggestionSpace={reserveSuggestionSpace}
      isContextFocused={isContextFocused}
      hasStarted={hasStarted}
      disabled={disabled}
      onSelectSuggestion={onSelectSuggestion}
      onSuggestionsShown={onSuggestionsShown}
      evidenceStarters={evidenceStarters}
      nurtureBanner={<RoutinesDesktopNurtureBanner />}
      shortcutTip={
        <WorkShortcutTip
          enabled={isTauri() && isCmdShiftHOverlayEnabled}
          isMac={isMacOS()}
        />
      }
    />
  );
};
